import styles from './Popup.module.scss';
import type { PopupAnimation } from './Popup.types';

export const ANIMATION_DURATION = 300;

/**
 * Returns true when the given hex/rgb color is perceptually dark
 * (relative luminance < 0.4). Falls back to false (assume light) for
 * unparseable values so the default dark icon is used.
 */
export const isDarkBackground = (color: string | null | undefined): boolean => {
  if (!color || typeof color !== 'string') return false;

  let r: number, g: number, b: number;

  const hex = color.replace('#', '');
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (/^[0-9a-f]{6}$/i.test(hex)) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else {
    const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (!match) return false;
    r = Number(match[1]);
    g = Number(match[2]);
    b = Number(match[3]);
  }

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.4;
};

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const getCloseDuration = (): number => (prefersReducedMotion() ? 0 : ANIMATION_DURATION);

const ANIMATION_CLASSES: Record<PopupAnimation, { enter: string; exit: string }> = {
  fade: { enter: styles.fadeIn, exit: styles.fadeOut },
  slideRight: { enter: styles.slideInRight, exit: styles.slideOutRight },
  slideDown: { enter: styles.slideInDown, exit: styles.slideOutDown },
};

export const getAnimationClasses = (
  animation: PopupAnimation | null | undefined,
  isClosing: boolean
): string => {
  const anim = animation ?? 'fade';
  const classes = ANIMATION_CLASSES[anim] ?? ANIMATION_CLASSES.fade;
  return isClosing ? classes.exit : classes.enter;
};
